/**
 * Favorites System
 *
 * Handles adding/removing listings to user favorites.
 * Uses REST API for backend communication.
 * Supports inline onclick and event delegation for AJAX-loaded content.
 */

/**
 * Toggle favorite status for a post.
 * This function is exposed globally for inline onclick handlers.
 *
 * @param {number} postId - Post ID to toggle
 * @returns {Promise<boolean>} False to prevent default action
 */
export async function toggleFavorite(postId) {
	postId = parseInt(postId);
	if (!postId) {
		console.error('GeoDir Favorites: Invalid post ID');
		return false;
	}

	// Find all favorite elements for this post
	const $elements = document.querySelectorAll(`.favorite_property_${postId} .geodir-act-fav`);
	if (!$elements.length) {
		console.warn(`GeoDir Favorites: No elements found for post ${postId}`);
		return false;
	}

	// Determine action from first element
	const isRemoving = $elements[0].classList.contains('geodir-removetofav-icon');
	const action = isRemoving ? 'remove' : 'add';

	// Show loading state
	$elements.forEach(el => {
		el.style.opacity = '0.6';
	});

	try {
		let response;

		if (action === 'add') {
			response = await window.GeoDir.api.post('favorites', { post_id: postId });
		} else {
			response = await window.GeoDir.api.delete(`favorites/${postId}`);
		}

		// Update all instances of this favorite button on the page
		updateFavoriteUI(postId, response.is_favorite);

		return false;
	} catch (error) {
		// Restore opacity
		$elements.forEach(el => {
			el.style.opacity = '1';
		});

		const errorMessage = window.geodir_params?.loading_listing_error_favorite || 'Failed to update favorite. Please try again.';
		alert(errorMessage);
		console.error('GeoDir Favorites Error:', error);
		return false;
	}
}

/**
 * Update UI for all favorite buttons of a given post.
 *
 * @param {number} postId - Post ID
 * @param {boolean} isFavorite - Whether post is now favorited
 */
function updateFavoriteUI(postId, isFavorite) {
	const wrappers = document.querySelectorAll(`.favorite_property_${postId}`);

	wrappers.forEach(wrapper => {
		const $el = wrapper.querySelector('.geodir-act-fav');
		if (!$el) return;

		// Restore opacity
		$el.style.opacity = '1';

		// Get icon and colors from data attributes
		const icon = $el.dataset.icon || window.geodir_params?.icon_fav || 'fas fa-heart';
		const textColor = $el.dataset.textColor || '';
		const textStyle = textColor ? ` style="color:${textColor};"` : '';

		// Get existing text classes
		const $textEl = $el.querySelector('.geodir-fav-text');
		const textClasses = $textEl ? $textEl.className : 'geodir-fav-text';

		// Dispose and hide any existing tooltip before updating content
		try {
			const tooltip = bootstrap?.Tooltip?.getInstance($el);
			if (tooltip) {
				tooltip.hide();
				tooltip.dispose();
			}
		} catch(err) {}

		if (isFavorite) {
			// Now favorited - show "remove" state
			const colorOn = $el.dataset.colorOn || '';
			const iconStyle = colorOn ? ` style="color:${colorOn}"` : '';
			const title = window.geodir_params?.text_remove_fav || 'Remove from Favorites';
			const text = window.geodir_params?.text_unfav || 'Unfavorite';

			$el.classList.remove('geodir-addtofav-icon');
			$el.classList.add('geodir-removetofav-icon');
			$el.setAttribute('title', title);
			$el.innerHTML = `<i${iconStyle} class="${icon}"></i> <span class="${textClasses}"${textStyle}> ${text}</span>`;
		} else {
			// Not favorited - show "add" state
			const colorOff = $el.dataset.colorOff || '';
			const iconStyle = colorOff ? ` style="color:${colorOff}"` : '';
			const title = window.geodir_params?.text_add_fav || 'Add to Favorites';
			const text = window.geodir_params?.text_fav || 'Favorite';

			$el.classList.remove('geodir-removetofav-icon');
			$el.classList.add('geodir-addtofav-icon');
			$el.setAttribute('title', title);
			$el.innerHTML = `<i${iconStyle} class="${icon}"></i> <span class="${textClasses}"${textStyle}> ${text}</span>`;
		}

		// Re-initialize tooltip on the updated element
		try {
			if (bootstrap?.Tooltip) {
				new bootstrap.Tooltip($el);
			}
		} catch(err) {}
	});
}

/**
 * Initialize favorites system.
 *
 * Note: We use inline onclick handlers for AJAX compatibility,
 * so event delegation is not needed here.
 */
export function initFavorites() {
	console.log('GeoDirectory: Favorites initialized');
}
